import { useMemo } from "react";
import { IDB } from "./IndexedDB/indexedDB";
import { EntityDataApi } from "./IndexedDB/IndexedDBApi";
import { useEntities, AsyncState, invalidateStores } from "./asyncData";
import Time from "../util/Time";
import { ExtendedDB } from "../types/extendedDB";
// 型のみインポート: Api.ts → IndexedDB → dbSlice の循環参照を避ける。
// SLUG_MAP は const オブジェクトのため値インポートでも循環の実害は無い(初期化順は後勝ち)。
import type { AuthedApi, EntityWriteKey } from "./Api";
import { SLUG_MAP } from "./Api";
import { keyToDataApi } from "./IndexedDB/generated/collectionMap";
import type { EntitySlugInfo } from "./IndexedDB/generated/entityDefs";
import { objectKeys } from "../util/typed";

export type SliceKey = keyof ExtendedDB;

// コレクションキーに対応する行の型
export type SliceRow<K extends SliceKey> = ExtendedDB[K][number];

// コレクションキー → 行配列 の部分集合(replaceCollections / loadDbSlice の入出力)。
// 具体的なキーでは Partial<ExtendedDB> と同一の型になるが、ジェネリックなキー K のまま
// 配列として扱える形(SliceRow<K>[])で宣言しておく(TS は ExtendedDB[K][number][] を
// ExtendedDB[K] へ単純化しないため、Partial<ExtendedDB> だと K のまま代入・filter ができない)。
export type SlicePatch = { [K in SliceKey]?: SliceRow<K>[] };

// ExtendedDB コレクションキー → 対応する IDB DataApi アクセサ。
// schema.json から生成された collectionMap を単一情報源とする(旧・手書きリテラルはステージ1で撤去。
// 等価性は src/__tests__/api/generatedRegistry.test.ts が凍結値と照合して担保)。
// 全キーが揃った完全な対応表(生成器が dbSlice 対象キーを網羅する)。
const KEY_TO_API: { [K in SliceKey]: (idb: IDB) => EntityDataApi<SliceRow<K>> } = keyToDataApi;

// SLUG_MAP(entities レジストリ対象のみの部分集合)を SliceKey で引くための型付きビュー。
const SLUG_INFO: Partial<Record<SliceKey, EntitySlugInfo>> = SLUG_MAP;

export const ALL_SLICE_KEYS: SliceKey[] = objectKeys(KEY_TO_API);

// out[key] への代入をキーと行型の相関を保ったまま行う(ループ内でキーが union に落ちるのを防ぐ)。
// TS はジェネリックなキーでの書き込み先を全キーの交差型として検査するため、
// 対象キー1つに絞った写像ビュー経由で代入する(値の型は SliceRow<K>[] のまま検査される)。
async function loadSliceKey<K extends SliceKey>(out: SlicePatch, idb: IDB, ownerId: string, key: K): Promise<void> {
  const slot: { [P in K]?: SliceRow<P>[] } = out;
  slot[key] = await KEY_TO_API[key](idb).getAllByOwner(ownerId, false);
}

// 指定キーを owner 別に IndexedDB から読み、Partial<ExtendedDB> へ組み立てる。
// filterDeleted=false: 論理削除行(deleted_at != null)も含めた全行をロードする。
export async function loadDbSlice(idb: IDB, ownerId: string, keys: SliceKey[]): Promise<Partial<ExtendedDB>> {
  const out: SlicePatch = {};
  await Promise.all(keys.map(async (key) => loadSliceKey(out, idb, ownerId, key)));
  return out;
}

export function sliceStores(idb: IDB, keys: SliceKey[]): string[] {
  return keys.map((key) => KEY_TO_API[key](idb).def.store);
}

// 差分計算・サーバー送信で必要になる行の共通形(全コレクションの行が満たす)
type DiffableRow = { id: string | number; updatedAt?: number; deleted_at?: number | null };

// push 失敗の通知コールバック
type PushErrorHandler = (e: unknown, ctx: { key: SliceKey; phase: string; row?: DiffableRow }) => void;

// IDB 上で差し替え対象コレクションの増減を比較し、サーバーへの送信に必要な差分を作る。
// 既存(beforeMap) と 次(rows) を id ベースで対比し、added / changed / removed を抽出する。
// removed: before に存在し next に居ない、または next 側で deleted_at がついた行。
// added:   next にしかいない、deleted_at の無い行。
// changed: id 一致で updatedAt が新しい、deleted_at の無い行。
type RowDiff<T extends DiffableRow> = { added: T[]; changed: T[]; removed: T[] };
function computeRowDiff<T extends DiffableRow>(before: readonly T[], next: readonly T[]): RowDiff<T> {
  const beforeMap = new Map<string | number, T>();
  for (const b of before) {
    beforeMap.set(b.id, b);
  }
  const nextMap = new Map<string | number, T>();
  for (const n of next) {
    nextMap.set(n.id, n);
  }
  const added: T[] = [];
  const changed: T[] = [];
  const removed: T[] = [];
  for (const nrow of next) {
    const prev = beforeMap.get(nrow.id);
    if (prev === undefined) {
      // before に居なかった: 新規。ただし deleted_at が立っているなら無視する。
      if (nrow.deleted_at == null) {
        added.push(nrow);
      }
      continue;
    }
    const prevDeleted = prev.deleted_at != null;
    const nextDeleted = nrow.deleted_at != null;
    if (!prevDeleted && nextDeleted) {
      // 論理削除に切り替わった行
      removed.push(nrow);
      continue;
    }
    if (!nextDeleted && (nrow.updatedAt ?? 0) > (prev.updatedAt ?? 0)) {
      changed.push(nrow);
    }
  }
  for (const brow of before) {
    if (!nextMap.has(brow.id) && brow.deleted_at == null) {
      // next から消えた(本来は論理削除運用なので稀)。論理削除として扱う。
      removed.push(brow);
    }
  }
  return { added, changed, removed };
}

// 1 コレクション分の差分をサーバーへ反映する。
// UUID + merge 対応のエンティティは「未削除な全行」をまとめて cachedAt 付きで送る。
// 整数マスタは 1 行ずつ put/patch/setDelete を送る。
// 失敗は onError へ渡しつつ全 op を試行し、失敗件数を返す(呼出元が全体失敗判定に使う)。
async function pushDiffToServer<T extends DiffableRow>(
  authedApi: AuthedApi,
  key: EntityWriteKey,
  info: EntitySlugInfo,
  diff: RowDiff<T>,
  activeRows: readonly T[],
  onError: PushErrorHandler,
): Promise<number> {
  let failures = 0;
  if (info.hasMerge) {
    if (diff.added.length === 0 && diff.changed.length === 0 && diff.removed.length === 0) {
      return 0;
    }
    const cachedAt = Time.Now().GetTime();
    // merge は base + cachedAt を一括送信。サーバー側で updatedAt 比較して取り込まれる。
    // deleted_at は現在 merge エンドポイントの schema に含まれているが、この経路(マスタ系スナップショット差分)
    // では従来どおり merge 対象から論理削除行を外し、削除は下の set-delete で個別に送る設計としている。
    const payload = activeRows.map((r) => {
      const { deleted_at, ...rest } = r;
      return { ...rest, cachedAt: cachedAt };
    });
    try {
      await authedApi.entities.merge(key, payload);
    } catch (e) {
      onError(e, { key: key, phase: "merge" });
      failures++;
    }
    // 論理削除分は merge の対象から外れるため別途 set-delete を送る。
    for (const rem of diff.removed) {
      try {
        await authedApi.entities.setDelete(key, rem.id, true);
      } catch (e) {
        onError(e, { key: key, phase: "setDelete", row: rem });
        failures++;
      }
    }
    return failures;
  }
  // 整数 PK マスタ: 1 行ずつ put/patch/setDelete
  for (const add of diff.added) {
    try {
      await authedApi.entities.putRow(key, add);
    } catch (e) {
      onError(e, { key: key, phase: "put", row: add });
      failures++;
    }
  }
  for (const chg of diff.changed) {
    try {
      await authedApi.entities.patchRow(key, chg);
    } catch (e) {
      onError(e, { key: key, phase: "patch", row: chg });
      failures++;
    }
  }
  for (const rem2 of diff.removed) {
    try {
      await authedApi.entities.setDelete(key, rem2.id, true);
    } catch (e) {
      onError(e, { key: key, phase: "setDelete", row: rem2 });
      failures++;
    }
  }
  return failures;
}

// オフラインで書き込もうとしたオンライン限定エンティティを表す例外。
// guardOnlineOnly が IDB 書込前に投げる。直前に showError 済みなので、
// 呼出元が catch しなくても画面状態は一貫(IDB 未書込)に保たれる。
export class OfflineOnlineOnlyWriteError extends Error {
  constructor(public key: SliceKey) {
    super(`オフラインのためこのデータ(${String(key)})は変更できません`);
    this.name = "OfflineOnlineOnlyWriteError";
  }
}

// オンライン中にサーバーへの書き込み(push)が一部でも失敗したことを表す例外。
// IDB は既にコミット済みのため、投げる前にサーバー最新状態をキャッシュへ取り直して
// (syncMastersFromServer)キャッシュをサーバーに追従させ、呼出元へ失敗を伝える。
export class ServerPushFailedError extends Error {
  constructor(public keys: SliceKey[]) {
    super(`サーバーへの保存に一部失敗しました(${keys.map(String).join(", ")})。最新状態に更新しました。`);
    this.name = "ServerPushFailedError";
  }
}

// オンライン限定キー(= replaceCollection 経由のオフライン書込を禁じる)か判定する。
// 判定基準は hasMerge:false(再送/merge 経路を持たない SLUG_MAP エンティティ)。
//   - 整数IDマスタ: getNextId(owner内 max+1)採番で、オフライン作成は同期時にサーバー採番IDと衝突する。
// いずれも作成/編集/削除すべてオンライン限定。
// merge 対応エンティティ(hasMerge:true)は client v7 採番 + 復帰時 merge replay でオフライン安全。
// constructionPlanAreaLocations / constructionPlanAreaPlannedMaterials / constructionActionAreas は
// UUID かつ hasMerge:true(親 area の専用トランザクション route 経由で子込み保存され、merge replay の対象)。
// materialQrs は SLUG_MAP 外(/merge-materials でサーバー採番されるトランザクション系)なので対象外。
function isOnlineOnlyKey(key: SliceKey): boolean {
  const info = SLUG_INFO[key];
  return info !== undefined && info.hasMerge === false;
}

// オフライン × オンライン限定キー の書き込みを IDB 反映前に拒否する。
// 1つでも対象キーが含まれていれば showError + throw し、部分書込みを防ぐ。
function guardOnlineOnly(authedApi: AuthedApi | undefined, keys: SliceKey[]): void {
  if (authedApi === undefined || authedApi.isOnline) {
    return;
  }
  const offending = keys.find((k) => isOnlineOnlyKey(k));
  if (offending !== undefined) {
    authedApi.showError("オフラインのため、このデータの追加・編集・削除はできません。オンラインに復帰してから操作してください。");
    throw new OfflineOnlineOnlyWriteError(offending);
  }
}

// SliceKey が entities レジストリ(SLUG_MAP)対象かを型述語で判定する(pushDiffToServer 送信対象の絞り込み)。
function isEntityWriteKey(key: SliceKey): key is SliceKey & EntityWriteKey {
  return Object.prototype.hasOwnProperty.call(SLUG_MAP, key);
}

// 1コレクションを owner 別に丸ごと差し替え、購読フックへ無効化通知する。
// rows は「差し替え後の全行(論理削除行も含む)」。
// authedApi が渡されかつオンラインなら、IDB 反映後に追加でサーバーへ書き込みを送る。
export async function replaceCollection<K extends SliceKey>(
  idb: IDB,
  ownerId: string,
  key: K,
  rows: SliceRow<K>[],
  authedApi?: AuthedApi,
  onError: PushErrorHandler = (e, ctx) => {
    console.error(`replaceCollection server push failed [${String(ctx.key)} / ${ctx.phase}]`, e);
  },
): Promise<void> {
  // オフラインでの整数IDマスタ書き込みを IDB 反映前に遮断する(最終防壁)。
  guardOnlineOnly(authedApi, [key]);
  const api = KEY_TO_API[key](idb);
  const slugInfo = SLUG_INFO[key];
  // 型述語による絞り込み(SLUG_MAP 登録キーのみ push 対象)。
  let writeKey: EntityWriteKey | undefined = undefined;
  if (isEntityWriteKey(key)) {
    writeKey = key;
  }
  // オンラインかつ push 対象(SLUG_MAP 登録済み)のときだけ値が入る絞り込み済み参照。
  // 以降の分岐はこの変数の undefined 判定だけで型が絞れる(表明を使わない)。
  let onlineApi: AuthedApi | undefined = undefined;
  if (authedApi !== undefined && authedApi.isOnline && slugInfo !== undefined && writeKey !== undefined) {
    onlineApi = authedApi;
  }
  // サーバー push 用の before スナップショットを取得する(IDB の現状)。
  // authedApi 未指定なら不要なので省略する。
  let before: SliceRow<K>[] = [];
  if (onlineApi !== undefined) {
    before = await api.getAllByOwner(ownerId, false);
  }
  await api.replaceOwnerRows(ownerId, rows, Time.Now().GetTime());
  invalidateStores(api.def.store);

  if (onlineApi !== undefined && slugInfo !== undefined && writeKey !== undefined) {
    const diff = computeRowDiff<SliceRow<K>>(before, rows);
    // materialQrs のみ deleted_at 列を持たないため、共通形(DiffableRow)経由で判定する
    const activeRows = rows.filter((r: DiffableRow) => r.deleted_at == null);
    const failures = await pushDiffToServer(onlineApi, writeKey, slugInfo, diff, activeRows, onError);
    if (failures > 0) {
      // オンライン中はキャッシュをサーバーに追従させる方針。push 失敗で IDB がサーバーと
      // 乖離するため、通常の get + cacheAll(syncMastersFromServer)でサーバー最新を取り直し、
      // 失敗を呼出元へ伝えて再試行を促す。
      await syncMastersFromServer(onlineApi, idb, ownerId);
      onlineApi.showError("サーバーへの保存に一部失敗しました。最新状態に更新したので、お手数ですが操作をやり直してください。");
      throw new ServerPushFailedError([key]);
    }
  }
}

// 複数コレクションをまとめて差し替え。多コレクション一括更新(区分複製/削除など)向け。
// authedApi が渡された場合、各コレクションについて差分をサーバーへ送信する。
export async function replaceCollections(
  idb: IDB,
  ownerId: string,
  updates: SlicePatch,
  authedApi?: AuthedApi,
  onError: PushErrorHandler = (e, ctx) => {
    console.error(`replaceCollections server push failed [${String(ctx.key)} / ${ctx.phase}]`, e);
  },
): Promise<void> {
  // オフラインでの整数IDマスタ書き込みを IDB 反映前に遮断する。
  // 全キーを書込前に検査し、1つでも対象なら部分書込みせず throw する。
  guardOnlineOnly(authedApi, objectKeys(updates));
  const now = Time.Now().GetTime();
  const perStore: { store: string; rows: DiffableRow[] }[] = [];
  const serverWriteWork: Array<{ key: SliceKey; writeKey: EntityWriteKey; info: EntitySlugInfo; diff: RowDiff<DiffableRow>; activeRows: DiffableRow[] }> = [];
  // オンライン時だけ値が入る絞り込み済み参照(以降の分岐で表明を使わないため)
  let onlineApi: AuthedApi | undefined = undefined;
  if (authedApi !== undefined && authedApi.isOnline) {
    onlineApi = authedApi;
  }

  // キーと行型の相関を保ったまま1コレクション分の収集を行う(ループ内で union に落とさない)
  async function collectOne<K extends SliceKey>(key: K): Promise<void> {
    const nextRows = updates[key];
    if (nextRows === undefined) {
      return;
    }
    const api = KEY_TO_API[key](idb);
    const slugInfo = SLUG_INFO[key];
    let writeKey: EntityWriteKey | undefined = undefined;
    if (isEntityWriteKey(key)) {
      writeKey = key;
    }
    if (onlineApi !== undefined && slugInfo !== undefined && writeKey !== undefined) {
      const before = await api.getAllByOwner(ownerId, false);
      const diff = computeRowDiff<SliceRow<K>>(before, nextRows);
      // materialQrs のみ deleted_at 列を持たないため、共通形(DiffableRow)経由で判定する
      const activeRows = nextRows.filter((r: DiffableRow) => r.deleted_at == null);
      serverWriteWork.push({ key, writeKey, info: slugInfo, diff, activeRows });
    }
    perStore.push({ store: api.def.store, rows: nextRows });
  }

  // 先に全コレクションの before スナップショット取得・差分計算と書込対象の収集を済ませる。
  // IDB への反映は後段で 1 トランザクションにまとめ、途中失敗による半端書込みを防ぐ。
  for (const key of objectKeys(updates)) {
    await collectOne(key);
  }
  // 複数ストアの差し替えを 1 トランザクションで原子的に反映する。
  await idb.replaceOwnerRowsAtomic(perStore, ownerId, now);
  invalidateStores(...perStore.map((entry) => entry.store));

  if (onlineApi !== undefined) {
    const failedKeys: SliceKey[] = [];
    for (const w of serverWriteWork) {
      const failures = await pushDiffToServer(onlineApi, w.writeKey, w.info, w.diff, w.activeRows, onError);
      if (failures > 0) {
        failedKeys.push(w.key);
      }
    }
    if (failedKeys.length > 0) {
      // push 失敗分は IDB がサーバーと乖離するため、通常の get + cacheAll でサーバー最新を取り直し追従させる。
      await syncMastersFromServer(onlineApi, idb, ownerId);
      onlineApi.showError("サーバーへの保存に一部失敗しました。最新状態に更新したので、お手数ですが操作をやり直してください。");
      throw new ServerPushFailedError(failedKeys);
    }
  }
}

// 整数PKマスタの次ID(owner内max+1, 非同期)。
export async function nextIdFor(idb: IDB, ownerId: string, key: SliceKey): Promise<number> {
  return await KEY_TO_API[key](idb).getNextId(ownerId);
}

// キーと行型が静的に結ばれた SlicePatch 組み立て(単一リテラル K の計算プロパティは
// Record<K, SliceRow<K>[]> に推論されるため表明不要)。
export function slicePatchOf<K extends SliceKey>(key: K, rows: SliceRow<K>[]): SlicePatch {
  return { [key]: rows };
}

// 動的キー駆動の画面(区分複製・CSV取込等)が、レコード形のまま任意コレクションへ書き戻すための境界。
// 行が key のコレクション由来であることは呼び出し側の構造(db[key] からの読み出し・複製)が保証する。
export function slicePatchOfRecords(key: SliceKey, rows: Array<Record<string, unknown> & { id: number | string }>): SlicePatch {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- union キーの計算プロパティ書き込みは交差型検査で表せず、レコード形→行型も静的に結べないため、境界をここへ集約する
  return { [key]: rows } as SlicePatch;
}


// 差分行のみ upsert。全行削除→再投入を避ける高速書込。
export async function upsertRows<K extends SliceKey>(idb: IDB, ownerId: string, key: K, rows: SliceRow<K>[]): Promise<void> {
  if (rows.length === 0) {
    return;
  }
  const api = KEY_TO_API[key](idb);
  await api.bulkPut(rows, { ownerId: ownerId }, Time.Now().GetTime());
  invalidateStores(api.def.store);
}

// 整数PKマスタ(SLUG_MAP idType:"int")のキー。SLUG_MAP のキーはすべて ExtendedDB のキーでもある。
// = サーバから取得してローカルキャッシュへ保存/更新する対象。マスタはオンライン限定のためマージ不要。
export const MASTER_SYNC_KEYS: SliceKey[] = objectKeys(SLUG_MAP).filter((k) => SLUG_MAP[k].idType === "int");

// 1マスタ分をサーバから取得してキャッシュへ反映する(キーと行型の相関を保つ)。
async function syncMasterKey<K extends SliceKey>(authedApi: AuthedApi, idb: IDB, ownerId: string, key: K, now: number): Promise<string> {
  const info = SLUG_INFO[key];
  if (info === undefined) {
    throw new Error(`${String(key)} は SLUG_MAP に存在しません。`);
  }
  // getRowsFromServer は行配列を未検証(unknown)で返す。サーバの /get-{plural} 応答契約
  // (audit:drift が監視)を前提に、このコレクションの行型として扱う。
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 未検証のサーバ応答をコレクション行型へ写す唯一の境界(検証導入は挙動変更のため見送り)
  const rows = (await authedApi.getRowsFromServer(info.plural)) as SliceRow<K>[];
  const api = KEY_TO_API[key](idb);
  await api.cacheAllAndReconcileMissing(rows, { ownerId }, now);
  return api.def.store;
}

// サーバからマスタを取得し、現在ユーザーの IDB キャッシュへ保存/更新する。
// 他データ(トランザクション)同様 cacheAllAndReconcileMissing で upsert する(未送信のローカル編集は
// canOverwriteByServerData で保護)。全件スナップショットのため、サーバー一覧から消えた行はローカルでも
// 論理削除扱いにする(削除のトゥームストーン伝播)。オフライン時は何もしない(既存キャッシュ維持)。
// 各マスタの失敗はスキップして続行する。
export async function syncMastersFromServer(authedApi: AuthedApi, idb: IDB, ownerId: string): Promise<void> {
  if (!authedApi.isOnline) {
    return;
  }
  const now = Time.Now().GetTime();
  const stores: string[] = [];
  await Promise.all(MASTER_SYNC_KEYS.map(async (key) => {
    try {
      stores.push(await syncMasterKey(authedApi, idb, ownerId, key, now));
    } catch (e) {
      console.warn(`マスタ同期に失敗しました: ${String(key)}`, e);
    }
  }));
  if (stores.length > 0) {
    invalidateStores(...stores);
  }
}

export type DbSliceState<K extends SliceKey> = AsyncState<Partial<ExtendedDB>> & { db: Pick<ExtendedDB, K> };

// ページ用フック。指定キーをロードし {db, loading, error, reload} を返す。
export function useDbSlice<K extends SliceKey>(idb: IDB | null, ownerId: string | null, keys: K[]): DbSliceState<K> {
  const keyStr = keys.join(",");
  const stores = useMemo(() => {
    if (idb) {
      return sliceStores(idb, keys);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idb, keyStr]);
  const enabled = !!idb && !!ownerId;
  const state = useEntities<Partial<ExtendedDB>>(
    stores,
    () => {
      if (idb === null || ownerId === null) {
        // enabled=false のためこのローダは呼ばれないが、型の絞り込みは分岐で行う(表明を使わない)
        return Promise.resolve({});
      }
      return loadDbSlice(idb, ownerId, keys);
    },
    [ownerId, keyStr],
    enabled
  );
  const emptyDefaults = useMemo(() => emptyDb(keys), [keyStr]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...state, db: mergeLoaded(emptyDefaults, state.data) };
}

// keys の全キーを空配列で埋めた db を作る。ジェネリックなキー配列に対する Pick の組み立ては
// TS が検証できないため、表明はこの関数に閉じ込める(直前のループが全キーを埋めている)。
function emptyDb<K extends SliceKey>(keys: K[]): Pick<ExtendedDB, K> {
  const d: SlicePatch = {};
  for (const key of keys) {
    d[key] = [];
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 直前のループが keys の全キーを空配列で埋めており、Pick の実体が保証されるため
  return d as Pick<ExtendedDB, K>;
}

// ロード結果(Partial)を既定値へ重ねる。スプレッドは optional キーを undefined 許容へ
// 広げてしまい TS では Pick へ戻せないため、表明をこの関数に閉じ込める。
function mergeLoaded<K extends SliceKey>(base: Pick<ExtendedDB, K>, loaded: Partial<ExtendedDB> | null | undefined): Pick<ExtendedDB, K> {
  if (loaded == null) {
    return base;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- loaded は loadDbSlice の結果でキー欠落はあっても undefined 値を持たないため、上書き後も Pick の実体が保たれる
  return { ...base, ...loaded } as Pick<ExtendedDB, K>;
}

export type FullDbState = AsyncState<Partial<ExtendedDB>> & { db: ExtendedDB };

// db 全体(ExtendedDB型)を要求するページ用(帳票exporter や動的アクセスのマスタ閲覧等)。
// ALL_SLICE_KEYS は全キーを含むため Pick<ExtendedDB, SliceKey> は ExtendedDB と同一の型になる(表明不要)。
export function useFullDb(idb: IDB | null, ownerId: string | null): FullDbState {
  const state = useDbSlice(idb, ownerId, ALL_SLICE_KEYS);
  return { ...state, db: state.db };
}
